import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { Seo, breadcrumbJsonLd, faqJsonLd } from "@/components/public/Seo";
import { GREEN, BLUE, ORANGE } from "@/components/landing/shared";

// Renderer generico delle pagine pubbliche: contenuti da publicContent.json,
// mai duplicati dalla homepage, gerarchia H1>H2, FAQ reali, CTA distinte.
export default function PublicPage({ content: c }) {
  const jsonLd = [
    breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: c.label, path: c.path }]),
    ...(c.faq?.length ? [faqJsonLd(c.faq)] : []),
  ];
  return (
    <PublicLayout breadcrumb={[{ name: c.label, path: c.path }]}>
      <Seo title={c.title} description={c.description} path={c.path} jsonLd={jsonLd} />
      <article data-testid={`public-page-${c.path.slice(1) || "home"}`}>
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
            style={{ background: "linear-gradient(120deg, #F2F9F5 0%, #FAFBFA 45%, #EFF3F8 100%)" }} />
          <div className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-14 pb-12">
            {c.kicker && (
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: ORANGE }}>
                {c.kicker}
              </p>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
              {c.h1}
            </h1>
            {c.intro.map((p) => (
              <p key={p.slice(0, 40)} className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">{p}</p>
            ))}
            <div className="mt-8 flex flex-wrap gap-3">
              {c.guide && (
                <Link to={c.guide.path} className="w-full inline-flex items-center gap-2 text-sm font-bold underline underline-offset-2 mb-1"
                  style={{ color: BLUE }} data-testid="page-guide-link">
                  <BookOpen className="w-4 h-4" aria-hidden="true" /> {c.guide.label}
                </Link>
              )}
              <Link to="/richiedi-demo"
                className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3.5 rounded-md text-white transition-transform hover:-translate-y-0.5"
                style={{ background: ORANGE }} data-testid="page-cta-demo">
                Richiedi una demo <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-md border-2 transition-colors"
                style={{ borderColor: BLUE, color: BLUE }} data-testid="page-cta-home">
                Scopri come funziona
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-5 lg:px-8 pb-8 space-y-14">
          {(c.sections || []).map((s) => (
            <section key={s.heading} className="scroll-mt-24">
              <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
                {s.heading}
              </h2>
              {s.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">{p}</p>
              ))}
            </section>
          ))}

          {c.faq?.length > 0 && (
            <section data-testid="public-faq">
              <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
                Domande frequenti
              </h2>
              <div className="mt-6 space-y-3">
                {c.faq.map((f) => (
                  <details key={f.q} className="rounded-xl border border-slate-200 bg-white group"
                    data-testid={`faq-${f.q.slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <summary className="cursor-pointer select-none px-5 py-4 text-sm font-bold text-slate-900 list-none flex items-center justify-between gap-3">
                      {f.q}
                      <span className="text-slate-400 group-open:rotate-90 transition-transform" aria-hidden="true">›</span>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        <section className="py-16" style={{ background: `linear-gradient(120deg, ${GREEN} 0%, #14564A 45%, ${BLUE} 100%)` }}>
          <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white"
              style={{ fontFamily: "Manrope, sans-serif" }}>
              Vuoi vedere come funziona sulla tua impresa?
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/85 max-w-xl mx-auto">
              Una demo guidata sul tuo caso: raccolta dati, VSME, dossier banca e molto altro.
            </p>
            <Link to="/richiedi-demo"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold px-8 py-4 rounded-md transition-transform hover:-translate-y-0.5"
              style={{ background: ORANGE, color: BLUE }} data-testid="page-cta-bottom">
              Richiedi una demo <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </article>
    </PublicLayout>
  );
}
